import { useEffect, useState, useRef, useMemo } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useIntl } from 'react-intl';
import { searchEntityRequest } from 'Redux/actions';
import {
    Modal,
    ModalBody,
    ModalFooter,
    Button,
    Form,
    Input,
    Select,
} from '@gpstrackit/react-controls';
import {
    FormControlLabel,
    Switch,
    Accordion,
    AccordionSummary,
    AccordionDetails,
    Icon,
    TextField,
} from '@mui/material';

import { createDealerWebhook, updateDealerWebhook, reset, openModalCreateEditWebHook } from './redux/slice';
import { NotificationManager } from 'Components/Notifications';

const CreateEditWebHook = ({ tableRef }) => {
    const dispatch = useDispatch();
    const { messages } = useIntl();
    const formRef = useRef(null);

    const { loading, openModal, result, dealerId, webhook, error } = useSelector(
        state => state.createEditWebHookRedux
    );
    const events = useSelector(state => state.generalRedux.events);

    const [formData, setFormData] = useState({
        name: '',
        url: '',
        eventType: '',
        status: 1,
        headers: '',
    });

    useEffect(() => {
        (!events || events.length === 0) && dispatch(searchEntityRequest({ key: "events" }));
    }, []);

    const jammingEvents = useMemo(
        () => (events || []).filter(e => e.name?.toLowerCase().includes('jamming')),
        [events]
    );

    useEffect(() => {
        if (webhook) {
            // When editing, find the full event object by matching the name from the webhook
            const eventObject = webhook.eventType
                ? jammingEvents.find(e => e.name === webhook.eventType)
                : null;

            setFormData({
                name: webhook.name || '',
                url: webhook.url || '',
                eventType: eventObject || '',
                status: webhook.status || 1,
                headers: webhook.headers || '',
            });
        } else {
            setFormData({
                name: '',
                url: '',
                eventType: '',
                status: 1,
                headers: '',
            });
        }
    }, [webhook, openModal, jammingEvents]);

    useEffect(() => {
        if (result?.success && !loading) {
            NotificationManager.success(
                webhook
                    ? messages['create_edit_webhook_updated'] || 'Webhook updated successfully'
                    : messages['create_edit_webhook_created'] || 'Webhook created successfully'
            );
            dispatch(reset({ openModal: false }));
            if (tableRef?.current?.getData) {
                tableRef.current.getData({});
            }
        }
    }, [result, loading]);

    useEffect(() => {
        if (error && !loading) {
            let errorMsg;
            switch (error.errorCode) {
                case 1010: // DUPLICATED_RECORD
                    errorMsg = messages['create_edit_webhook_duplicate_error'] || 'A webhook for this event type already exists. Edit it instead.';
                    break;
                case 1011: // INVALID_PARAMS
                    errorMsg = messages['create_edit_webhook_invalid_params'] || 'Invalid parameters. Please check your input.';
                    break;
                case 401: // UNAUTHORIZED
                    errorMsg = messages['create_edit_webhook_unauthorized'] || 'You are not authorized to perform this action.';
                    break;
                case 1002: // RECORD_NOT_EXIST
                    errorMsg = messages['create_edit_webhook_record_not_exist'] || 'The webhook or dealer does not exist.';
                    break;
                default:
                    errorMsg = messages['create_edit_webhook_error'] || error.message || 'Failed to save webhook. Please try again.';
            }
            NotificationManager.error(errorMsg);
        }
    }, [error, loading, messages]);

    const isValidUrl = (urlString) => {
        try {
            new URL(urlString);
            return true;
        } catch (e) {
            return false;
        }
    };

    const handleSave = () => {
        // eventType can be an object {id, name, color} or a string
        // We need to keep it as an object for the backend to validate by ID
        const payload = {
            dealerId,
            name: formData.name,
            url: formData.url,
            eventType: formData.eventType,
            status: formData.status ? 1 : 0,
        };

        if (formData.headers?.trim()) {
            try {
                payload.headers = JSON.parse(formData.headers);
            } catch (e) {
                NotificationManager.error('Invalid JSON format for headers');
                return;
            }
        }

        if (webhook?.id) {
            dispatch(updateDealerWebhook({ ...payload, id: webhook.id }));
        } else {
            dispatch(createDealerWebhook(payload));
        }
    };

    const handleClose = () => {
        dispatch(reset({ openModal: false }));
    };

    return (
        <Modal
            open={openModal}
            title={webhook ? messages['create_edit_webhook_edit_title'] || 'Edit Webhook' : messages['create_edit_webhook_create_title'] || 'Create Webhook'}
            position="right"
            size="small"
            handleClose={handleClose}
            loading={loading}
        >
            <ModalBody>
                <Form
                    ref={formRef}
                    onSubmit={() => true}
                    onValidSubmit={handleSave}
                >
                    <div style={{ padding: '0 10px', marginBottom: 30 }}>
                        <div style={{ margin: 10 }}>
                            <Input
                                type="text"
                                label={messages['create_edit_webhook_name'] || 'Name'}
                                value={formData.name}
                                onChange={(value) => setFormData({ ...formData, name: value })}
                                required
                                requiredMessage={messages['create_edit_webhook_name_required'] || 'Name is required'}
                                minLength={1}
                                maxLength={255}
                            />
                        </div>

                        <div style={{ margin: 10 }}>
                            <Input
                                type="url"
                                label={messages['create_edit_webhook_action'] || 'Action (Webhook URL)'}
                                value={formData.url}
                                onChange={(value) => setFormData({ ...formData, url: value })}
                                required
                                requiredMessage={messages['create_edit_webhook_url_required'] || 'URL is required'}
                                placeholder="https://example.com/webhook"
                                customValidation={(value) => {
                                    if (value && !isValidUrl(value)) {
                                        return messages['create_edit_webhook_url_invalid'] || 'Invalid URL format';
                                    }
                                    return true;
                                }}
                            />
                        </div>

                        <div style={{ margin: 10 }}>
                            <Select
                                id="eventType"
                                label={messages['create_edit_webhook_event_type'] || 'Event Type'}
                                value={formData.eventType}
                                onChange={(value) => setFormData({ ...formData, eventType: value })}
                                options={jammingEvents}
                                textField="name"
                                required
                                requiredMessage={messages['create_edit_webhook_event_type_required'] || 'Event Type is required'}
                            />
                        </div>

                        <div style={{ margin: 10 }}>
                            <FormControlLabel
                                control={
                                    <Switch
                                        checked={formData.status === 1}
                                        onChange={(e) => setFormData({ ...formData, status: e.target.checked ? 1 : 0 })}
                                    />
                                }
                                label={messages['create_edit_webhook_status'] || 'Active'}
                            />
                        </div>

                        <div style={{ margin: 10 }}>
                            <Accordion defaultExpanded={false}>
                                <AccordionSummary expandIcon={<Icon>expand_more</Icon>}>
                                    {messages['create_edit_webhook_headers'] || 'Advanced: Headers (Optional)'}
                                </AccordionSummary>
                                <AccordionDetails>
                                    <TextField
                                        label="Headers (JSON)"
                                        value={formData.headers}
                                        onChange={(e) => setFormData({ ...formData, headers: e.target.value })}
                                        fullWidth
                                        multiline
                                        rows={4}
                                        placeholder='{"Authorization": "Bearer token"}'
                                        helperText={messages['create_edit_webhook_headers_help'] || 'Optional custom headers in JSON format'}
                                    />
                                </AccordionDetails>
                            </Accordion>
                        </div>
                    </div>
                </Form>
            </ModalBody>

            <ModalFooter>
                <Button
                    className="btn btn-cancel"
                    onClick={handleClose}
                >
                    {messages['common_cancel'] || 'Cancel'}
                </Button>
                <Button
                    className="btn btn-dark-blue"
                    onClick={() => formRef?.current?.submit()}
                    disabled={loading}
                >
                    {messages['common_save'] || 'Save'}
                </Button>
            </ModalFooter>
        </Modal>
    );
};

export default CreateEditWebHook;
